import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const RUNTIME_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Floor plans banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A staged or furnished apartment interior photo — living room, kitchen, bedroom, or model unit — that is unique to this page. Prefer virtually staged or furnished photography when available. Never use logos, brand marks, floor plan drawings, line art, or unit layout graphics.',
    context: 'Full-bleed banner hero on the Availability and Pricing / Floor Plans page. Use a staged unit interior only. Prefer virtually staged or furnished model photos when available. Never use a floor plan drawing, line art, site plan, map, exterior, amenity shot, or logo. Must be a different photo from every other page hero, especially Virtual Tours. Do not reuse other page hero images.',
  },
};

export function FloorPlansHero() {
  const bgImage = useSlotGroup('floorplanshero.bg_image', RUNTIME_DEF);

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup groupPath="floorplanshero.bg_image" definition={RUNTIME_DEF} className="absolute inset-0">
        {bgImage.image ? (
          <VibesitesImage url={bgImage.image} alt={bgImage.alt || 'Floor plans banner'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/30" />
      </EditableSlotGroup>

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4" style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}>
        <RichTextContent path="floorplanshero.title" as="h1" className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider" />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansHero',
  displayName: 'Floor Plans Hero',
  tokens: {
    'floorplanshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Floor Plans</p>',
    },
  },
  slotGroups: {
    'floorplanshero.bg_image': RUNTIME_DEF,
  },
};

(FloorPlansHero as any).contentSchema = contentSchema;

export default FloorPlansHero;
